import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Building2, Moon, Route, Target } from 'lucide-react';
import { api } from '../../lib/api';
import {
  KpiCard, KpiRow, ChartCard, TabLoading, TabError, EmptyChart, CHART, ANIM, RATE_COLORS, truncate, uur1,
  TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE, TOOLTIP_CURSOR, GRID_STROKE, AXIS_PROPS, LEGEND_PROPS,
} from './StatsShared';

/** Breedte van de naamkolom: past op een telefoon van 390px. */
const NAME_WIDTH = 124;
const NAME_CHARS = 18;
const hoursFmt = uur1;

export function ObjectenTab({ year }: { year: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stats-objecten', year],
    queryFn: () => api.stats.objecten(year),
  });

  if (isLoading) return <TabLoading />;
  if (error || !data) return <TabError error={error} />;

  const top10 = data.objects.slice(0, 10).map((o) => ({
    name: truncate(o.object_naam, NAME_CHARS),
    full: o.object_naam,
    Uren: Math.round(o.hours * 10) / 10,
    Diensten: o.shifts,
  }));

  const kmData = data.objects
    .filter((o) => (o.km_total ?? 0) > 0)
    .slice(0, 10)
    .map((o) => ({
      name: truncate(o.object_naam, NAME_CHARS),
      full: o.object_naam,
      km: Math.round(o.km_total),
    }));

  const nachtData = data.objects
    .filter((o) => (o.nacht_hours ?? 0) > 0)
    .slice(0, 10)
    .map((o) => ({
      name: truncate(o.object_naam, NAME_CHARS),
      full: o.object_naam,
      Nacht: Math.round(o.nacht_hours * 10) / 10,
      Weekend: Math.round(o.weekend_hours * 10) / 10,
    }));

  const fullLabel = (_: unknown, payload: Array<{ payload?: { full?: string } }>) =>
    payload?.[0]?.payload?.full ?? '';

  return (
    <div className="space-y-4 sm:space-y-6">
      <KpiRow>
        <KpiCard icon={Building2} label="Verschillende objecten" kpi={data.kpis.unieke_objecten} fmt="int" prevYear={year - 1} />
        <KpiCard icon={Target} label="Aandeel vaste plek" kpi={data.kpis.top_object_pct} fmt="pct" prevYear={year - 1} />
        <KpiCard icon={Route} label="Gereden kilometers" kpi={data.kpis.totaal_km} fmt="km" prevYear={year - 1} />
        <KpiCard icon={Moon} label="Nachturen" kpi={data.kpis.nacht_uren} fmt="hours" prevYear={year - 1} />
      </KpiRow>

      <ChartCard title="Meeste uren per object" subtitle="Top 10" height={Math.max(200, top10.length * 34 + 30)}>
        {top10.length === 0 ? (
          <EmptyChart>Nog geen diensten op een object in {year}.</EmptyChart>
        ) : (
          <ResponsiveContainer>
            <BarChart data={top10} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} horizontal={false} />
              <XAxis type="number" {...AXIS_PROPS} />
              <YAxis dataKey="name" type="category" {...AXIS_PROPS} width={NAME_WIDTH} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={TOOLTIP_CURSOR} labelFormatter={fullLabel} formatter={(v: unknown, n: string) => (n === 'Uren' ? hoursFmt(v) : String(v))} />
              <Bar dataKey="Uren" fill={CHART.main} radius={[0, 4, 4, 0]} barSize={18} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Kilometers per object" subtitle="Heen en terug, opgeteld over al je diensten" height={Math.max(200, kmData.length * 34 + 30)}>
        {kmData.length === 0 ? (
          <EmptyChart>Geen afstanden bekend. Vul bij je objecten de afstand vanaf huis in.</EmptyChart>
        ) : (
          <ResponsiveContainer>
            <BarChart data={kmData} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} horizontal={false} />
              <XAxis type="number" {...AXIS_PROPS} />
              <YAxis dataKey="name" type="category" {...AXIS_PROPS} width={NAME_WIDTH} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={TOOLTIP_CURSOR} labelFormatter={fullLabel} formatter={(v: unknown) => `${Number(v).toLocaleString('nl-NL')} km`} />
              <Bar dataKey="km" name="Kilometers" fill={CHART.main} radius={[0, 4, 4, 0]} barSize={18} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Nacht- en weekenduren per object" height={Math.max(220, nachtData.length * 34 + 60)}>
        {nachtData.length === 0 ? (
          <EmptyChart>Geen nacht- of weekenduren in {year}.</EmptyChart>
        ) : (
          <ResponsiveContainer>
            <BarChart data={nachtData} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} horizontal={false} />
              <XAxis type="number" {...AXIS_PROPS} />
              <YAxis dataKey="name" type="category" {...AXIS_PROPS} width={NAME_WIDTH} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={TOOLTIP_CURSOR} labelFormatter={fullLabel} formatter={hoursFmt} />
              <Legend {...LEGEND_PROPS} />
              <Bar dataKey="Nacht" stackId="o" fill={RATE_COLORS.nacht} barSize={18} isAnimationActive={false} />
              <Bar dataKey="Weekend" stackId="o" fill={RATE_COLORS.weekend} radius={[0, 4, 4, 0]} barSize={18} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}
